import { api } from "@/lib/api";

export interface HzzStatus { code: string; name_hr: string; color: string }
export interface HzzRequest {
  id: string; hzz_id: string; number: string | null; worker_id: string | null;
  process_id: string | null; worker_name: string; request_type: string;
  change_type: string | null; occupation: string | null; submitted_on: string | null;
  status_code: string; status_since: string; supplement_required: boolean | null;
  requested_from: string | null; requested_to: string | null;
  opinion_on: string | null; opinion_valid_to: string | null;
  permit_valid_from: string | null; permit_valid_to: string | null;
  source: string; imported_at: string; last_checked_at: string; last_observed_at: string;
}
export interface HzzHistory {
  id: string; request_id: string; old_status: string | null; new_status: string;
  observed_at: string; source: string; import_id: string;
}
export interface HzzChoice { hzz_id: string; worker_id?: string; skip?: boolean }
export interface HzzPreviewRow {
  hzz_id: string; number: string | null; worker_name: string; worker_id: string | null;
  match_method: string; action: "new" | "changed" | "unchanged" | "stale" | "skipped";
  old_status: string | null; new_status: string; warning: string | null;
  citizenship_iso2: string | null; citizenship_label: string | null;
  current_citizenship_iso2: string | null; citizenship_will_fill: boolean;
  citizenship_mismatch: boolean; citizenship_warning: string | null;
}
export interface HzzPreview {
  file_sha256: string; exported_at: string; counts: Record<string, number>;
  rows: HzzPreviewRow[]; import_id: string | null; citizenship_summary: string;
}
export type HzzCategory = "a" | "b" | "c" | "d" | "e" | "f";
export interface HzzControlRow {
  worker_id: string | null; worker_name: string; oib: string | null;
  citizenship: string | null; citizenship_mismatch: boolean; worker_category: string | null;
  active: boolean | null; sites_today: string[]; categories: HzzCategory[];
  latest_request: HzzRequest | null; permit_id: string | null; permit_number: string | null;
  permit_valid_to: string | null; may_work: "da" | "ne" | "provjeriti"; problems: string[];
}
export interface HzzControl {
  as_of: string; pending_days: number; counts: Record<string, number>;
  total: number; items: HzzControlRow[];
}
export const hzzRequestTypes: Record<string, string> = {
  BEZ_TTR: "Novo zapošljavanje bez testa tržišta rada", SA_TTR: "Novo zapošljavanje s testom tržišta rada",
  SEZONSKI: "Sezonski rad", SEZONSKI_VISEGODISNJI: "Višegodišnji sezonski rad",
  DODATNI_RAD: "Dodatni rad", PROMJENA: "Promjena",
};
export const hzzChangeTypes: Record<string, string> = {
  PROMJENA_POSLODAVAC: "Promjena poslodavca", PROMJENA_POSLODAVCA_I_ZANIMANJA: "Promjena poslodavca i zanimanja",
  PROMJENA_ZANIMANJA: "Promjena zanimanja", PROMJENA_MJESTA_RADA: "Promjena mjesta rada",
  PROMJENA_KORISNIKA_KOD_AGENCIJE: "Promjena korisnika kod agencije",
};
export function hzzRequestType(request: HzzRequest) {
  return hzzChangeTypes[request.change_type ?? ""] ?? hzzRequestTypes[request.request_type] ?? "Vrsta nije poznata · provjeriti";
}
export function hzzCategories(pendingDays: number): Record<HzzCategory, string> {
  return {
    a: "Aktivan bez važeće dozvole", b: "HZZ izdana, nema dozvole u aplikaciji", c: "Rok se razlikuje",
    d: `Bez ishoda dulje od ${pendingDays} dana`, e: "Bivši radnik · kandidat za odjavu", f: "Osoba nije u aplikaciji",
  };
}
export async function getHzzStatuses(signal?: AbortSignal) {
  return (await api.get<HzzStatus[]>("/hzz/statusi", { signal })).data;
}
export async function getHzzRequests(workerId: string, signal?: AbortSignal) {
  const result: HzzRequest[] = [];
  for (let offset = 0; ; offset += 500) {
    const { data } = await api.get<HzzRequest[]>("/hzz/zahtjevi", { params: { worker_id: workerId, offset, limit: 500 }, signal });
    result.push(...data);
    if (data.length < 500) return result;
  }
}
export async function getHzzHistory(requestId: string, signal?: AbortSignal) {
  return (await api.get<HzzHistory[]>(`/hzz/zahtjevi/${requestId}/povijest`, { signal })).data;
}
export async function previewHzz(file: File, choices: HzzChoice[], signal?: AbortSignal) {
  const body = new FormData(); body.append("file", file); body.append("choices", JSON.stringify(choices));
  return (await api.post<HzzPreview>("/hzz/uvoz/preview", body, { signal })).data;
}
export async function commitHzz(file: File, preview: HzzPreview, choices: HzzChoice[]) {
  const body = new FormData(); body.append("file", file); body.append("file_sha256", preview.file_sha256);
  body.append("choices", JSON.stringify(choices));
  return (await api.post<HzzPreview>("/hzz/uvoz/commit", body)).data;
}
export async function getHzzControl(category: HzzCategory | null, offset: number, signal?: AbortSignal) {
  return (await api.get<HzzControl>("/hzz/kontrola", { params: { category: category ?? undefined, offset, limit: 100 }, signal })).data;
}
export async function exportHzz() {
  const { data } = await api.get<Blob>("/hzz/izvoz.xlsx", { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement("a"); anchor.href = url; anchor.download = "hzz-kontrola.xlsx";
  document.body.append(anchor); anchor.click(); anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
